import React from 'react';

const PreviousEdition: React.FC = () => {
  return (
    <section className="py-24 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            Language Intelligence 2024
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
            Relive the highlights from our previous conference
          </p>
        </div>

        {/* Photo Gallery */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="grid grid-cols-1 gap-6">
              <div className="overflow-hidden rounded-xl shadow-lg h-64">
                <img 
                  src="/OpeningPanel800.jpg" 
                  alt="Opening Panel Discussion" 
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-xl shadow-lg h-64">
                <img 
                  src="/Audience800.jpg" 
                  alt="Conference Audience" 
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <div className="overflow-hidden rounded-xl shadow-lg h-64">
                <img 
                  src="/porscheberns.jpg" 
                  alt="Industry Use Cases" 
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="overflow-hidden rounded-xl shadow-lg h-64">
                <img 
                  src="/KirtiStefaniaLaura800.jpg" 
                  alt="Panel Discussion" 
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-10">
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-[#00529B]">
              A Successful Previous Edition
            </h3>
            <p className="text-gray-600">
              The Language Intelligence Conference 2024 brought together industry leaders, researchers, and practitioners
              to explore cutting-edge developments in multilingual AI and language technology.
            </p>
            <p className="text-gray-600">
              Held on November 19-20, 2024, the conference featured expert panels, technical presentations, and
              networking opportunities that shaped the future of language intelligence.
            </p>
            <div className="mt-8">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">Key Highlights:</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-[#00529B] mt-1 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Opening panel on "Highlights of the Year" with industry experts</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-[#00529B] mt-1 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Use cases from pharma, automotive, retail, healthcare, security, and media sectors</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-[#00529B] mt-1 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Technical sessions on generative AI, terminology tools, and language resources</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-[#00529B] mt-1 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Closing panel on EU AI Act and technical/legal/ethical challenges</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="mb-6">
              <div className="w-full">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#00529B] mb-3">Programme Highlights</h3>
                  <p className="text-gray-600">
                    Two days of expert talks, panels, and networking
                  </p>
                </div>
                <div className="mt-6 space-y-4 text-left">
                  <div className="bg-blue-50 p-5 rounded-lg">
                    <p className="font-semibold text-[#00529B] text-lg">Day 1 - November 19, 2024</p>
                    <ul className="text-md text-gray-700 mt-3 ml-5 list-disc space-y-2">
                      <li>Opening Panel: "Highlights of the Year"</li>
                      <li>Technology Trends Outlook</li>
                      <li>Use Cases in Pharma & Automotive</li>
                      <li>Retail & Healthcare Applications</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-5 rounded-lg">
                    <p className="font-semibold text-[#00529B] text-lg">Day 2 - November 20, 2024</p>
                    <ul className="text-md text-gray-700 mt-3 ml-5 list-disc space-y-2">
                      <li>Security & Defence Use Cases</li>
                      <li>Media Applications</li>
                      <li>AI in Interpreting Industry</li>
                      <li>EU AI Act Panel Discussion</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center mt-6">
              <p className="text-gray-600 mb-5">
                Join us this year for another exceptional conference experience!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/LI2024_programme_20241106.pdf"
                  download="LI2024_Programme.pdf"
                  className="inline-flex items-center px-6 py-3 border border-[#00529B] text-base font-medium rounded-md shadow-sm text-[#00529B] bg-white hover:bg-[#00529B] hover:text-white transition-colors duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download 2024 Programme
                </a>
                <a
                  href="https://www.youtube.com/playlist?list=PLfoGiILhKUfF1sJKgBsFvmdKYNMa7fpW1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#00529B] hover:bg-[#003D74] transition-colors duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YouTube Playlist
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-6">
            Our previous edition was supported by industry-leading sponsors
          </p>
          <div className="flex flex-wrap justify-center gap-8 items-center">
            <div className="grayscale hover:grayscale-0 transition-all bg-white p-4 rounded-lg shadow-sm">
              <img src="/Yaraku.png" alt="Yaraku" className="h-16 w-auto" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all bg-white p-4 rounded-lg shadow-sm">
              <img src="/Almawave_0.png" alt="Almawave" className="h-16 w-auto" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all bg-white p-4 rounded-lg shadow-sm">
              <img src="/logo_TermSolutions_200.png" alt="Term Solutions" className="h-16 w-auto" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all bg-white p-4 rounded-lg shadow-sm">
              <img src="/logo_Translated_200.png" alt="Translated" className="h-16 w-auto" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all bg-white p-4 rounded-lg shadow-sm">
              <img src="/Interprefy.png" alt="Interprefy" className="h-16 w-auto" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PreviousEdition;
